import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    mode: String,
    redirectUrl: String
  }

  connect() {
    this.running = false
  }

  disconnect() {
    clearInterval(this.requestTimer)
  }

  async submit(event) {
    if (this.running) {
      event.preventDefault()
      return
    }

    const fileInput = this.element.querySelector('input[type="file"][multiple]')
    if (!fileInput) return

    const files = Array.from(fileInput.files || []).filter(file => file && file.size >= 0)
    if (files.length === 0) return

    event.preventDefault()
    this.running = true

    const submitButton = this.element.querySelector('button[type="submit"], input[type="submit"]')
    if (submitButton) submitButton.disabled = true

    const total = files.length
    let uploaded = 0
    let queued = 0
    let failed = 0
    const csrfToken = this.csrfToken()
    const fileFieldName = fileInput.name
    const batchId = this.randomBatchId()

    this.renderStatus({
      stage: "running",
      total: total,
      uploaded: uploaded,
      queued: queued,
      failed: failed,
      currentFilename: null,
      message: `Starting ${this.modeLabel()} upload for ${total} files`
    })

    for (let index = 0; index < files.length; index += 1) {
      const file = files[index]
      const currentFilename = file.name
      try {
        const formData = this.buildBaseFormData()
        formData.append(fileFieldName, file, file.name)
        formData.append("client_batch_upload", "true")
        formData.append("client_batch_id", batchId)
        formData.append("client_batch_total", String(total))
        formData.append("client_batch_index", String(index + 1))
        formData.append("client_batch_queued_before", String(queued))
        formData.append("client_batch_failed_before", String(failed))

        const response = await this.uploadFile(formData, file, csrfToken)

        if (!response.ok) {
          failed += 1
        } else {
          queued += 1
        }
      } catch (_error) {
        failed += 1
      } finally {
        clearInterval(this.requestTimer)
        uploaded += 1
        this.renderStatus({
          stage: "running",
          total: total,
          uploaded: uploaded,
          queued: queued,
          failed: failed,
          currentFilename: currentFilename,
          message: `Uploaded ${uploaded}/${total} files`
        })
      }
    }

    this.running = false
    if (submitButton) submitButton.disabled = false
    this.requestStatus().textContent = `Upload finished: ${queued} queued, ${failed} failed.`
    const completedStage = queued === 0 ? "failed" : failed > 0 ? "completed_with_errors" : "completed"
    this.renderStatus({
      stage: completedStage,
      total: total,
      uploaded: uploaded,
      queued: queued,
      failed: failed,
      currentFilename: null,
      message: `Finished upload: queued ${queued}, failed ${failed}`
    })

    if (failed > 0) return

    setTimeout(() => {
      const redirectTo = this.redirectUrlValue || window.location.href
      window.location.assign(redirectTo)
    }, 700)
  }

  uploadFile(formData, file, csrfToken) {
    const startedAt = Date.now()
    let phase = `Sending ${file.name}`
    const update = () => {
      const seconds = Math.floor((Date.now() - startedAt) / 1000)
      this.requestStatus().textContent = `${phase} — ${seconds}s elapsed.`
    }
    update()
    this.requestTimer = setInterval(update, 1000)

    // Byte transfer and server acknowledgement are separate phases. A busy
    // server may not send a Turbo status until long after the file was sent.
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest()
      request.open("POST", this.element.action)
      request.setRequestHeader("Accept", "application/json")
      if (csrfToken) request.setRequestHeader("X-CSRF-Token", csrfToken)
      request.upload.addEventListener("progress", event => {
        if (event.lengthComputable) {
          phase = `Sending ${file.name}: ${Math.round(event.loaded * 100 / event.total)}% transferred`
          update()
        }
      })
      request.upload.addEventListener("load", () => {
        phase = `${file.name} sent; waiting for the server to save and queue it`
        update()
      })
      request.addEventListener("load", () => resolve({ ok: request.status >= 200 && request.status < 300 }))
      request.addEventListener("error", () => reject(new Error("Upload connection failed")))
      request.addEventListener("abort", () => reject(new Error("Upload cancelled")))
      request.send(formData)
    })
  }

  requestStatus() {
    let status = this.element.querySelector("[data-upload-request-status]")
    if (!status) {
      status = document.createElement("div")
      status.dataset.uploadRequestStatus = ""
      status.className = "small mt-2"
      status.setAttribute("role", "status")
      this.element.append(status)
    }
    return status
  }

  buildBaseFormData() {
    const formData = new FormData()
    const elements = Array.from(this.element.elements || [])
    elements.forEach((element) => {
      if (!element || !element.name || element.disabled) return
      if (element.type === "file") return
      if ((element.type === "checkbox" || element.type === "radio") && !element.checked) return
      formData.append(element.name, element.value)
    })
    return formData
  }

  renderStatus({ stage, total, uploaded, queued, failed, currentFilename, message }) {
    const host = document.getElementById("construction_drawings_batch_status")
    if (!host) return

    const percent = total > 0 ? Math.min(100, Math.max(0, Math.round((uploaded / total) * 100))) : 0
    const badgeClass = stage === "failed"
      ? "text-bg-danger"
      : stage === "completed"
        ? "text-bg-success"
        : stage === "completed_with_errors"
          ? "text-bg-warning"
          : "text-bg-warning"
    const barClass = stage === "failed"
      ? "bg-danger"
      : stage === "completed" || stage === "completed_with_errors"
        ? "bg-success"
        : "bg-warning"

    host.innerHTML = `
      <div class="card shadow-sm border-0">
        <div class="card-header d-flex flex-wrap justify-content-between align-items-center gap-2 py-2">
          <span class="fw-semibold">${this.modeLabel()} Status</span>
          <span class="badge ${badgeClass}">${this.humanizeStage(stage)}</span>
        </div>
        <div class="card-body py-2">
          <div class="small text-muted mb-2">${this.escapeHtml(message || "Status update received.")}</div>
          <div class="progress mb-2" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${percent}">
            <div class="progress-bar ${barClass}" style="width: ${percent}%;">${percent}%</div>
          </div>
          <div class="small text-muted d-flex flex-wrap gap-3">
            <span>Uploaded: <strong>${uploaded}/${total}</strong></span>
            <span>Queued: <strong>${queued}</strong></span>
            <span>Failed: <strong>${failed}</strong></span>
            ${currentFilename ? `<span>Current: <strong>${this.escapeHtml(currentFilename)}</strong></span>` : ""}
          </div>
        </div>
      </div>
    `
  }

  modeLabel() {
    return this.modeValue === "batch_pretrain" ? "Batch Training Ingest" : "Ingest Upload"
  }

  randomBatchId() {
    const mode = this.modeValue || "batch"
    const randomSuffix = Math.random().toString(16).slice(2, 10)
    return `${mode}-${Date.now()}-${randomSuffix}`
  }

  humanizeStage(stage) {
    return String(stage || "running")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase())
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.getAttribute("content") : ""
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll("\"", "&quot;")
      .replaceAll("'", "&#39;")
  }
}
